import * as highcharts from "highcharts";
import { Component, h } from "preact";
import colors from "../utils/colors";

export interface IBarChartProps {
  alignThresholds?: boolean;
  data: Highcharts.SeriesOptionsType[];
  symbolLabel?: string;
  height?: number;
  xLabels: string[];
  width?: number;
  yAxis?: Highcharts.AxisOptions[];
  colors?: string[];
}

export interface IMinMax {
  min: number;
  max: number;
}

export default class BarChart extends Component<IBarChartProps> {
  private divElement?: HTMLDivElement;
  private chart?: Highcharts.Chart;
  private reflowIntervalHandle?: number;

  public componentDidMount() {
    this.createChart(this.props.data, this.props.symbolLabel);

    this.reflowIntervalHandle = window.setInterval(() => {
      if (this.chart !== undefined) {
        this.chart.reflow();
      }
    }, 500);
  }

  public componentDidUpdate(prevProps: any, prevState: any) {
    if (this.props !== prevProps || this.state !== prevState) {
      this.createChart(this.props.data, this.props.symbolLabel);
    }
  }

  // Destroy chart before unmount.
  public componentWillUnmount() {
    window.clearInterval(this.reflowIntervalHandle as number);
    if (this.chart) {
      this.chart.destroy();
    }
  }

  public render(props: IBarChartProps, state: {}) {
    return (
      <div
        id="container"
        style="height: 100%; width: 100%"
        ref={e => (this.divElement = e as HTMLDivElement)}
      />
    );
  }

  public getChartOptions(): Highcharts.Options {
    const currentYAxis =
      this.props.yAxis === undefined
        ? [
            {
              title: {
                text: undefined,
              },
            },
          ]
        : this.props.yAxis;

    let localColors = this.props.colors;
    if (!this.props.colors) {
      localColors = colors;
    }

    return {
      chart: {
        alignTicks: this.props.alignThresholds,
        renderTo: this.divElement,
        type: "column",
      },
      colors: localColors,
      credits: {
        enabled: false,
      },
      series: this.props.data,
      // required to remove title
      title: {
        text: "",
      },
      tooltip: {
        borderColor: "#ccc",
        footerFormat: "</table>",
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat:
          '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
          '<td style="padding:0" align="right"><b>{point.y}</b></td></tr>',
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories: this.props.xLabels,
      },
      yAxis: currentYAxis,
    };
  }

  private createChart(
    data: Highcharts.SeriesOptionsType[],
    symbolLabel?: string
  ) {
    const symbolText = !symbolLabel ? "K" : symbolLabel;

    // define default suffix
    data.forEach(serie => {
      if (serie.tooltip === undefined) {
        serie.tooltip = { valueSuffix: `${symbolText}€` };
      }
    });

    highcharts.setOptions({
      lang: {
        decimalPoint: ".",
        numericSymbols: [" K", " M", " G", " T", " P", " E"],
        thousandsSep: ",",
      },
    });

    const options = this.getChartOptions();
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new highcharts.Chart(options);
  }
}
